import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import TierRows from "@/Layouts/components/Tiers/TierRows";
import TierExpandableHeader from "@/Layouts/components/Tiers/TierExpandableHeader";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// Custom components
import GrossProfitTier from "./GrossProfitTier";

// MUI
import { Collapse, Divider, Grid, Stack, Typography } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

const saleTier = (inputs) => {
	const { retail, lease, cash } = inputs;
	return {
		from: {
			input: "Dollar",
			value: 0,
		},
		to: {
			input: "Dollar",
			value: 100000,
		},
		retail: {
			input: retail,
			value: 0,
		},
		lease: {
			input: lease,
			value: 0,
		},
		cash: {
			input: cash,
			value: 0,
		},
		miniDeal: {
			input: "Dollar",
			value: 0,
		},
	};
};

const getTierSalesInputs = (tier) => {
	const salesIdentifiers = ["retail", "lease", "cash"];
	return salesIdentifiers?.reduce((acc, key) => {
		acc[key] = tier?.[key]?.["input"] || "Percent";
		return acc;
	}, {});
};

export default function FrontEndGrossProfit(props) {
	const { title = "", tiers = [], readOnly = false, onActionUpdate = () => {}, onActionValidation = () => {} } = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	const rowsErrors = errors?.get("compensation");
	const columnsErrors = errors?.get("commissions");
	const miniDealsErrors = errors?.get("miniDeals");

	// Control to send update Actions / References
	const wasUpdated = useRef(false);

	const onModelUpdate = (updatedTiers) => {
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(updatedTiers);
		}
	};

	// States
	const [innerTiers, setInnerTiers] = useCallbackState([], onModelUpdate);

	const updateTiers = (updatedTiers) => {
		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	const handleAddTier = () => {
		const updatedTiers = [...innerTiers];
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 0;
		const inputs = getTierSalesInputs(updatedTiers?.[lastIndex]);

		if (updatedTiers?.length) {
			updatedTiers[lastIndex].to.value = 0;
		}

		updatedTiers.push(saleTier(inputs));
		wasUpdated.current = false;
		setInnerTiers(updatedTiers);
	};

	const handleRemoveTier = () => {
		const updatedTiers = [...innerTiers];
		if (updatedTiers.length > 1) {
			updatedTiers.splice(-1);
		}
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 0;
		updatedTiers[lastIndex].to.value = 100000;
		updateTiers(updatedTiers);
	};

	const handleUpdateValue = (index, area, value, delta = 0) => {
		const updatedTiers = [...innerTiers];
		updatedTiers[index][area]["value"] = value ? Number(value) : 0;
		if (updatedTiers?.[index + 1] && area === "to") {
			updatedTiers[index + 1]["from"]["value"] = Number(value) + delta;
		}
		updateTiers(updatedTiers);
	};

	const handleChangeType = (_, area, input) => {
		const updatedTiers = [...innerTiers];
		updatedTiers.forEach((tier) => {
			tier[area]["input"] = input;
			tier[area]["value"] = 0;
		});
		updateTiers(updatedTiers);
	};

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "compensation",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Unit: 0.01, Dollar: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=",
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: (results = new Map(), sections = []) => {
									if (!results?.size || sections?.length < 2) {
										return [];
									}

									const columns = results?.get("COLUMNS") || [];
									const lastIndex = sections?.length - 1;
									const someDefinition = Object.entries(sections[lastIndex]).some(([, item]) => item.value);

									if (!someDefinition) {
										const keys = Object.keys(sections[lastIndex]);
										keys.forEach((key) => {
											const item = sections[lastIndex][key];
											if (!item?.value) {
												columns[lastIndex][key] = true;
											}
										});
									}

									return [...columns];
								},
							},
						},
						errorsMap: ["retail", "lease", "cash"],
						message: "Invalid commissions",
						validationType: "COMPOSED",
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "miniDeals",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
								},
								{
									type: "STEP",
									on: "value",
									compare: "<=", // If current (upper) < next is good
									deltas: { Dollar: 0 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["miniDeal"],
						message: "Invalid mini deal definition",
						validationType: "SIMPLE",
					},
					section: innerTiers || [],
				},
			]);
		}
	}, [innerTiers, validate]);

	useEffect(() => {
		setInnerTiers(clone(tiers));

		return () => {
			setInnerTiers([]);
			wasUpdated.current = false;
		};
	}, [tiers, setInnerTiers]);

	return (
		<Stack width={1} my={4}>
			<Stack>
				<Divider className="!border-disabled" />
			</Stack>
			<Stack mt={2}>
				<Stack>
					<Typography className="!font-montserrat" variant={"h6"} color="var(--text-color)" paddingTop={"3px"}>
						{title}
					</Typography>
				</Stack>
				<Stack mt={2.8} alignItems={"center"} justifyContent={"center"} style={{ maxWidth: "829.5px" }}>
					<Typography variant="h5" component="div" className={"tiered-title"} style={{ fontSize: "17px" }}>
						Sale Type
					</Typography>
				</Stack>
				<Stack mt={1.5}>
					<TierRows
						deleteButtonText={"Remove Tier"}
						addButtonText={"Add Tier"}
						deleteButtonDisabled={Boolean(innerTiers?.length <= 1) || readOnly}
						addButtonDisabled={readOnly}
						spaces={{
							mainSection: 255,
							columns: 120,
							totalColumns: 4,
							totalRows: innerTiers?.length || 1,
							separation: 10,
						}}
						containerProps={{
							position: "relative",
							mt: 0.5,
						}}
						onClickDelete={handleRemoveTier}
						onClickAdd={handleAddTier}
					>
						<TierExpandableHeader
							headers={[
								{
									title: "Front PVR",
									titleClassName: "tiered-title !text-[14px]",
									style: { minWidth: "255px" },
								},
								{
									title: "Retail Comm.",
									titleClassName: "tiered-title !text-[14px] !-ml-[4px]",
									showDivider: true,
									itemProps: { sm: 12, md: 1.5 },
									style: { minWidth: "150px" },
								},
								{
									title: "Lease Comm.",
									titleClassName: "tiered-title !text-[14px] !-ml-[5px]",
									showDivider: true,
									itemProps: { sm: 12, md: 1.5, ml: "-5px" },
								},
								{
									title: "Cash Comm.",
									titleClassName: "tiered-title !text-[14px]",
									showDivider: true,
									itemProps: { sm: 12, md: 1.5, ml: "-5px" },
								},
								{
									title: "Mini Deal",
									titleClassName: "tiered-title !text-[14px]",
									showDivider: true,
									itemProps: { sm: 12, md: 1.5, ml: "-5px" },
								},
							]}
							containerProps={{
								direction: "row",
								justifyContent: "flex-start",
								columnSpacing: 4,
							}}
						/>

						<TransitionGroup>
							<Collapse>
								<Stack direction="column" rowGap={2} mt={1}>
									{innerTiers?.map((tier, key) => {
										const fromDisabled = Boolean(!key);
										const toDisabled = Boolean(innerTiers?.length === key + 1);
										const itemKey = `gross_profit_tier_${key}`;
										return (
											<Grid key={itemKey} container item>
												<GrossProfitTier
													index={key}
													item={tier}
													fromDisabled={fromDisabled}
													toDisabled={toDisabled}
													readOnly={readOnly}
													spaces={{
														mainSection: 255,
														columns: 120,
														totalColumns: 4,
														totalRows: innerTiers?.length || 1,
														separation: 5.5,
														reduction: 0,
													}}
													rowsErrors={rowsErrors}
													columnsErrors={columnsErrors}
													miniDealsErrors={miniDealsErrors}
													spacing={3.8}
													sxMainContent={{ minWidth: "227.5px" }}
													sxCells={{ minWidth: "109px" }}
													onActionUpdate={handleUpdateValue}
													onChangeType={handleChangeType}
												/>
											</Grid>
										);
									})}
								</Stack>
							</Collapse>
						</TransitionGroup>

						{Boolean(localErrorsMessages?.length) && (
							<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
								{localErrorsMessages.map((message, key) => (
									<TierAlertError key={`error_front_gross_profit_${message}_${key}`} text={message} />
								))}
							</Grid>
						)}
					</TierRows>
				</Stack>
			</Stack>
		</Stack>
	);
}
FrontEndGrossProfit.propTypes = {
	title: PropTypes.string,
	tiers: PropTypes.array,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
